import type { TypeScriptCustomGraderSpec } from '../../../services/grader.interface.js';
import { useStyles } from '../GraderPage.style.js';

export interface Field {
  path: string;
  value: unknown;
}

export function label(path: string): string {
  return path
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\[(\d+)\]/g, (_, index: string) => ` ${Number(index) + 1}`)
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' · ');
}

export function display(value: unknown): string {
  return typeof value === 'string'
    ? value
    : (JSON.stringify(value, null, 2) ?? String(value));
}

export function fields(init: TypeScriptCustomGraderSpec['init']) {
  const settings: Field[] = [];
  const prompts: Field[] = [];
  const formats: Field[] = [];

  function visit(value: unknown, path: string, isPrompt: boolean): void {
    const key = path.split('.').at(-1) ?? '';
    if (/response(?:Format|Schema)$/i.test(key)) {
      formats.push({ path, value });
    } else if (Array.isArray(value)) {
      if (key === 'messages' || isPrompt) {
        value.forEach((item, index) =>
          visit(item, `${path}[${index}]`, isPrompt),
        );
      } else {
        settings.push({ path, value });
      }
    } else if (value !== null && typeof value === 'object') {
      Object.entries(value).forEach(([child, item]) =>
        visit(item, path ? `${path}.${child}` : child, isPrompt),
      );
    } else if (isPrompt) {
      prompts.push({ path, value });
    } else {
      settings.push({ path, value });
    }
  }

  Object.entries(init).forEach(([key, value]) => {
    visit(
      value,
      key,
      key === 'prompts' ||
        key === 'messages' ||
        key === 'user' ||
        /prompt$/i.test(key),
    );
  });
  return { settings, prompts, formats };
}

export function TypeScriptGraderConfiguration({
  spec,
}: {
  spec: TypeScriptCustomGraderSpec;
}) {
  const styles = useStyles();
  const { settings, formats } = fields(spec.init);
  const bindings = Object.entries(spec.invoke ?? {});

  return (
    <>
      <section className={styles.overviewCard} aria-label="Configuration">
        <div className={styles.overviewCardTitle}>Configuration</div>
        <dl className={styles.readOnlyDetails}>
          {spec.evaluationModel && (
            <div>
              <dt className={styles.readOnlyLabel}>Evaluation model</dt>
              <dd className={styles.readOnlyValue}>{spec.evaluationModel}</dd>
            </div>
          )}
          {settings.map(({ path, value }) => (
            <div key={path}>
              <dt className={styles.readOnlyLabel}>{label(path)}</dt>
              <dd className={styles.readOnlyValue}>{display(value)}</dd>
            </div>
          ))}
        </dl>
      </section>

      {formats.length > 0 && (
        <section className={styles.overviewCard} aria-label="Output formats">
          <div className={styles.overviewCardTitle}>Output formats</div>
          {formats.map(({ path, value }) => (
            <details key={path}>
              <summary>{label(path)}</summary>
              <pre className={styles.readOnlyText}>{display(value)}</pre>
            </details>
          ))}
        </section>
      )}

      {bindings.length > 0 && (
        <section className={styles.overviewCard} aria-label="Invocation inputs">
          <div className={styles.overviewCardTitle}>Invocation inputs</div>
          <dl className={styles.readOnlyDetails}>
            {bindings.map(([name, value]) => (
              <div key={name}>
                <dt className={styles.readOnlyLabel}>{label(name)}</dt>
                <dd className={styles.readOnlyValue}>{display(value)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </>
  );
}
